// Checkout logic shared by the hosted site and the widget. Pure functions:
// what to offer, in which order, and what the one-tap booking would use.
// The server stays the judge of every booking and payment.

/** A pass the studio sells. */
export type BuyablePass = {
  id: string;
  title: string;
  description?: string | null;
  price: number;
  /** null = unlimited. */
  credits: number | null;
  durationDays: number | null;
  isOneTimeUse: boolean;
  /** A single class sold as a 1-credit pass (passes.is_drop_in). */
  isDropIn?: boolean | null;
  status: string;
};

/**
 * A single class, stored as a 1-credit pass: it pays for one class and never
 * shows as a pass. Only the flag decides; a real 1-class pass (a gift, a kids
 * pass) stays a pass.
 */
export function isDropInPass(pass: { isDropIn?: boolean | null }): boolean {
  return pass.isDropIn === true;
}

/** A pass or membership the student already holds. */
export type HeldCredit = {
  id: string;
  kind: "pass" | "membership";
  title: string;
  status: string;
  /** null = unlimited. */
  creditsRemaining: number | null;
  /** Pass: effective expiry. Membership: end of the current period. */
  expiresAt: string | null;
  /** Pass type, for workshops that accept only some passes. */
  passId?: string | null;
};

export type PassGroup = "trial" | "single" | "classPasses" | "unlimited";

export type PassOption = BuyablePass & {
  group: PassGroup;
  /** Class passes only: price divided by classes. */
  perClass: number | null;
  bestValue: boolean;
};

export const PASS_GROUP_ORDER: PassGroup[] = ["trial", "single", "classPasses", "unlimited"];

export function passGroup(pass: Pick<BuyablePass, "isOneTimeUse" | "credits" | "isDropIn">): PassGroup {
  if (pass.isOneTimeUse) return "trial";
  if (isDropInPass(pass)) return "single";
  if (pass.credits === null) return "unlimited";
  // Without the flag (older API answers), one credit still reads as a single class.
  if (pass.isDropIn == null && pass.credits <= 1) return "single";
  return "classPasses";
}

/**
 * Passes worth showing for a class, grouped and sorted: trial, single class,
 * class passes (fewest classes first), unlimited. Inactive passes and a trial
 * the student already bought are left out.
 */
export function groupPassOptions(
  passes: BuyablePass[],
  alreadyBoughtPassIds: Iterable<string> = [],
): { group: PassGroup; options: PassOption[] }[] {
  const bought = new Set(alreadyBoughtPassIds);
  const options: PassOption[] = passes
    .filter((p) => p.status === "ACTIVE")
    .filter((p) => !(p.isOneTimeUse && bought.has(p.id)))
    .map((p) => {
      const group = passGroup(p);
      return {
        ...p,
        group,
        perClass: group === "classPasses" && p.credits ? p.price / p.credits : null,
        bestValue: false,
      };
    });

  const best = bestValuePass(options);
  if (best) best.bestValue = true;

  return PASS_GROUP_ORDER.map((group) => ({
    group,
    options: options
      .filter((o) => o.group === group)
      .sort((a, b) =>
        group === "classPasses"
          ? (a.credits ?? 0) - (b.credits ?? 0) || a.price - b.price
          : a.price - b.price,
      ),
  })).filter((g) => g.options.length > 0);
}

/**
 * The class pass with the lowest price per class, if it actually saves money:
 * cheaper per class than a single class, or than the other class passes.
 */
export function bestValuePass<T extends { perClass: number | null; group: string; price: number }>(
  options: T[],
): T | null {
  const classPasses = options.filter((o) => o.perClass !== null);
  if (classPasses.length === 0) return null;

  const cheapest = classPasses.reduce((a, b) => (b.perClass! < a.perClass! ? b : a));
  const singles = options.filter((o) => o.group === "single");
  const cheapestSingle = singles.length ? Math.min(...singles.map((s) => s.price)) : null;
  const others = classPasses.filter((o) => o !== cheapest);

  const beatsSingle = cheapestSingle !== null && cheapest.perClass! < cheapestSingle;
  const beatsOthers = others.length > 0 && others.every((o) => cheapest.perClass! < o.perClass!);
  return beatsSingle || beatsOthers ? cheapest : null;
}

/** Credits the student can spend on a class starting at `startsAt`. */
export function usableCredits(held: HeldCredit[], startsAt: string | Date): HeldCredit[] {
  const start = new Date(startsAt).getTime();
  return held.filter(
    (h) =>
      h.status === "ACTIVE" &&
      (h.creditsRemaining === null || h.creditsRemaining > 0) &&
      (h.expiresAt === null || new Date(h.expiresAt).getTime() >= start),
  );
}

/**
 * Workshop booked with a pass: only the accepted passes that hold enough
 * credits for everyone. Unlimited passes and memberships never count (the
 * studio would give the workshop away).
 */
export function usableWorkshopCredits(
  held: HeldCredit[],
  startsAt: string | Date,
  rule: { passCredits: number | null; acceptedPassIds: string[] },
  people = 1,
): HeldCredit[] {
  if (!rule.passCredits) return [];
  return usableCredits(held, startsAt).filter(
    (h) =>
      h.kind === "pass" &&
      !!h.passId &&
      rule.acceptedPassIds.includes(h.passId) &&
      h.creditsRemaining !== null &&
      h.creditsRemaining >= rule.passCredits! * people,
  );
}

/**
 * The pass or membership a one-tap booking uses: the one that expires first,
 * so nothing goes to waste. Unlimited ones count as expiring at their end date.
 */
export function pickQuickBookCredit(
  held: HeldCredit[],
  startsAt: string | Date,
): { credit: HeldCredit; creditsAfter: number | null } | null {
  const usable = usableCredits(held, startsAt).sort((a, b) => {
    const ea = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
    const eb = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
    if (ea !== eb) return ea - eb;
    // Same expiry: spend a limited pass before an unlimited one.
    return (a.creditsRemaining ?? Infinity) - (b.creditsRemaining ?? Infinity);
  });
  const credit = usable[0];
  if (!credit) return null;
  return {
    credit,
    creditsAfter: credit.creditsRemaining === null ? null : credit.creditsRemaining - 1,
  };
}

/** The studio's payment settings, as GET /payment-settings returns them. */
export type PaymentSettings = {
  onlinePayments: boolean;
  isTwintEnabledInStripe?: boolean | null;
  twint: boolean;
  twintPhoneNumber?: string | null;
  twintQrCode?: string | null;
  payLater: boolean;
  bankTransfer: boolean;
  bankDetails?: string | null;
};

export type PaymentChoice = "twint" | "card" | "cash" | "transfer";

export type PaymentOption = {
  choice: PaymentChoice;
  /** Value the checkout API expects in `paymentMethod`. */
  apiMethod: "CARD" | "TWINT" | "CASH" | "TRANSFER";
  /** Stripe Checkout opens on this method only. */
  preferredMethod?: "twint" | "card";
  /** Paid outside BookZen (TWINT to the studio's number, cash, transfer). */
  manual: boolean;
};

/**
 * Payment methods in display order: Stripe first (TWINT, card), then what the
 * studio confirms itself (TWINT to its number, cash, transfer). TWINT through
 * Stripe is confirmed instantly, so it wins over the studio's own TWINT
 * number when both are on.
 */
export function paymentOptions(settings: PaymentSettings | null | undefined): PaymentOption[] {
  if (!settings) return [];
  const options: PaymentOption[] = [];
  const stripeTwint = settings.onlinePayments && !!settings.isTwintEnabledInStripe;

  if (stripeTwint) {
    options.push({ choice: "twint", apiMethod: "CARD", preferredMethod: "twint", manual: false });
  }
  if (settings.onlinePayments) {
    options.push({ choice: "card", apiMethod: "CARD", preferredMethod: "card", manual: false });
  }
  if (!stripeTwint && settings.twint) {
    options.push({ choice: "twint", apiMethod: "TWINT", manual: true });
  }
  if (settings.payLater) options.push({ choice: "cash", apiMethod: "CASH", manual: true });
  if (settings.bankTransfer) {
    options.push({ choice: "transfer", apiMethod: "TRANSFER", manual: true });
  }
  return options;
}

/**
 * The method selected when the checkout opens: Stripe (TWINT, then card) when
 * the studio takes it, so nobody lands on "TWINT to the studio's phone" under
 * "Secure payment by Stripe" (Run 4). Otherwise the first manual method.
 */
export function defaultPaymentChoice(options: PaymentOption[]): PaymentChoice | null {
  return (options.find((o) => !o.manual) ?? options[0])?.choice ?? null;
}

/**
 * Free cancellation window, as the booking service applies it today
 * (determine-cancellation-window.ts): 24 h for classes, 48 h for workshops
 * and retreats. Keep both in step.
 */
export function freeCancellationHours(type: string | null | undefined): number {
  return type === "WORKSHOP" || type === "RETREAT" ? 48 : 24;
}

export function freeCancellationUntil(startsAt: string | Date, type?: string | null): Date {
  return new Date(new Date(startsAt).getTime() - freeCancellationHours(type) * 3600_000);
}

/** Free cancellation of one class, as the API sends it with the class or booking. */
export type Cancellation = {
  /** ISO time until which cancelling is free. */
  freeUntil: string;
  /** Still free right now (server clock). */
  isFree: boolean;
};

/**
 * The one rule (Run 5): free while `now` is at or before the deadline. The
 * cancel endpoint, the texts (popup, checkout, confirmation, e-mail, My
 * bookings) all read it, so what the student reads is what cancelling does.
 */
export function cancellationPolicy(
  startsAt: string | Date,
  type?: string | null,
  now: Date = new Date(),
): Cancellation {
  const freeUntil = freeCancellationUntil(startsAt, type);
  return { freeUntil: freeUntil.toISOString(), isFree: now.getTime() <= freeUntil.getTime() };
}
